<template>
  <div class="product-detail">
    <!-- 顶部导航组件 -->
    <TheHeader />
    
    <!-- 面包屑导航 -->
    <div class="breadcrumb-container">
      <div class="breadcrumb-content">
        <router-link to="/">首页</router-link>
        <span class="separator">/</span>
        <router-link :to="{ name: 'Products' }">产品中心</router-link>
        <span class="separator">/</span>
        <span class="current">{{ product?.name || '产品详情' }}</span>
      </div>
    </div>
    
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <el-icon class="loading-icon"><Loading /></el-icon>
      <p>正在加载药品信息...</p>
    </div>
    
    <!-- 错误提示 -->
    <div v-else-if="error" class="error-container">
      <el-icon class="error-icon"><WarningFilled /></el-icon>
      <p>{{ error }}</p>
      <div class="error-actions">
        <button class="retry-btn" @click="loadProductData">重试</button>
        <router-link :to="{ name: 'Products' }" class="back-btn">返回产品中心</router-link>
      </div>
    </div>
    
    <!-- 药品详情内容 -->
    <div v-else-if="product" class="product-content">
      <!-- 药品基本信息 -->
      <div class="product-basic">
        <div class="product-gallery">
          <!-- 主图 -->
          <div class="main-image">
            <img :src="getFullImageUrl(displayedImage)" :alt="product.name" />
          </div>
          <!-- 小图列表 -->
          <div v-if="product.images && product.images.length > 1" class="image-thumbs">
            <div 
              v-for="(image, index) in product.images" 
              :key="index" 
              class="thumb" 
              :class="{ active: displayedImage === image }"
              @click="displayedImage = image"
            >
              <img :src="getFullImageUrl(image)" :alt="`${product.name} ${index + 1}`" />
            </div>
          </div>
        </div>
        
        <div class="product-info">
          <h1 class="product-name">{{ product.name }}</h1>
          
          <div class="product-meta">
            <div class="meta-item" v-if="product.category">
              <span class="meta-label">产品分类:</span>
              <span class="meta-value">
                <span class="category-tag">{{ product.category.name }}</span>
              </span>
            </div>
            
            <div class="meta-item" v-if="product.manufacturer">
              <span class="meta-label">生产厂家:</span>
              <span class="meta-value">{{ product.manufacturer }}</span>
            </div>
            
            <div class="meta-item" v-if="product.approval_number">
              <span class="meta-label">批准文号:</span>
              <span class="meta-value">{{ product.approval_number }}</span>
            </div>
          </div>
          
          <div class="product-indications" v-if="product.indications">
            <h3>适应症</h3>
            <div class="indications-content" v-html="product.indications"></div>
          </div>
        </div>
      </div>
      
      <!-- 药品详细信息 -->
      <div class="product-details">
        <div class="details-tabs">
          <div 
            class="tab" 
            :class="{ active: activeTab === 'description' }" 
            @click="activeTab = 'description'"
          >
            产品描述
          </div>
          <div 
            class="tab" 
            :class="{ active: activeTab === 'ingredients' }" 
            @click="activeTab = 'ingredients'"
          >
            成分信息
          </div>
          <div 
            class="tab" 
            :class="{ active: activeTab === 'usage' }" 
            @click="activeTab = 'usage'"
          >
            用法用量
          </div>
        </div>
        
        <div class="tab-content">
          <!-- 产品描述 -->
          <div v-show="activeTab === 'description'" class="tab-pane">
            <div class="html-content" v-html="product.description || '暂无产品描述'"></div>
          </div>
          
          <!-- 成分信息 -->
          <div v-show="activeTab === 'ingredients'" class="tab-pane">
            <div class="html-content" v-html="product.ingredients || '暂无成分信息'"></div>
          </div>
          
          <!-- 用法用量 -->
          <div v-show="activeTab === 'usage'" class="tab-pane">
            <div class="html-content" v-html="product.usage || '暂无用法用量信息'"></div>
          </div>
        </div>
      </div>
      
      <!-- 相关推荐 -->
      <div class="related-products" v-if="relatedProducts.length > 0">
        <h2>相关产品推荐</h2>
        <div class="related-grid">
          <div class="product-card" v-for="item in relatedProducts" :key="item.id">
            <div class="product-image">
              <img :src="getFullImageUrl(item.image) || placeholderImages.med1" :alt="item.name" />
              <div class="product-hover">
                <router-link :to="{ name: 'ProductDetail', params: { id: item.id } }" class="detail-view-btn">
                  查看详情
                </router-link>
              </div>
            </div>
            <div class="product-info">
              <h3>{{ item.name }}</h3>
              <div class="meta-item" v-if="item.manufacturer">
                <span class="meta-label">生产厂家:</span>
                <span class="meta-value">{{ getShortManufacturer(item.manufacturer) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 底部信息组件 -->
    <TheFooter />
  </div>
</template>

<script>
// 导入Element Plus组件
import { ElIcon } from 'element-plus'
import { Loading, WarningFilled } from '@element-plus/icons-vue'
import 'element-plus/dist/index.css'

// 导入占位图片
import { placeholderImages } from '@/assets/placeholder.js'

// 导入自定义组件
import TheHeader from '@/components/TheHeader.vue'
import TheFooter from '@/components/TheFooter.vue'

// 导入API
import { getMedicineDetail, getMedicinesByCategory, BASE_URL } from '@/api/config'

export default {
  name: 'ProductDetail',
  components: {
    ElIcon,
    Loading,
    WarningFilled,
    TheHeader,
    TheFooter
  },
  data() {
    return {
      // 产品数据
      product: null,
      loading: true,
      error: null,
      
      // 图片展示
      displayedImage: '',
      
      // 相关产品
      relatedProducts: [],
      
      // 激活的标签
      activeTab: 'description',
      
      // 占位图片
      placeholderImages
    }
  },
  computed: {
    // 获取产品ID
    productId() {
      return this.$route.params.id
    }
  },
  methods: {
    // 加载产品数据
    async loadProductData() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await getMedicineDetail(this.productId);
        
        if (response.code === 1) {
          this.product = response.data;
          this.displayedImage = this.product.image;
          
          // 加载相关产品
          if (this.product.category_id) {
            this.loadRelatedProducts(this.product.category_id);
          }
        } else {
          this.error = response.msg || '获取药品详情失败';
        }
      } catch (error) {
        console.error('获取药品详情错误:', error);
        this.error = '网络错误，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    
    // 加载相关产品
    async loadRelatedProducts(categoryId) {
      try {
        const response = await getMedicinesByCategory(categoryId, 1, 4);
        
        if (response.code === 1 && response.data.data) {
          // 排除当前产品
          this.relatedProducts = response.data.data
            .filter(item => item.id !== this.product.id)
            .slice(0, 3); // 最多显示3个相关产品
        }
      } catch (error) {
        console.error('获取相关产品错误:', error);
      }
    },
    
    // 获取完整图片URL
    getFullImageUrl(path) {
      if (!path) return null;
      
      // 如果已经是完整URL则直接返回
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      
      // 否则拼接基础URL
      return `${BASE_URL}${path}`;
    },
    
    // 提取短厂商名称
    getShortManufacturer(manufacturer) {
      if (!manufacturer) return '暂无';
      return manufacturer.length > 10 ? manufacturer.substring(0, 10) + '...' : manufacturer;
    }
  },
  watch: {
    // 监听路由参数变化，重新加载数据
    '$route.params.id': function() {
      this.loadProductData();
    }
  },
  created() {
    this.loadProductData();
  }
}
</script>

<style scoped>
.product-detail {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  color: #333;
  line-height: 1.6;
  background-color: #f9fafc;
}

/* 面包屑导航 */
.breadcrumb-container {
  background-color: #fff;
  border-bottom: 1px solid #f0f0f0;
  padding: 120px 0 15px;
}

.breadcrumb-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  font-size: 0.9rem;
  color: #666;
}

.breadcrumb-content a {
  color: #666;
  text-decoration: none;
  transition: color 0.3s;
}

.breadcrumb-content a:hover {
  color: #3a8ee6;
}

.separator {
  margin: 0 8px;
}

.current {
  color: #3a8ee6;
}

/* 加载和错误状态样式 */
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  margin: 50px 0;
}

.loading-icon,
.error-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.loading-icon {
  color: #3a8ee6;
  animation: rotate 2s linear infinite;
}

.error-icon {
  color: #f56c6c;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.error-actions {
  margin-top: 25px;
  display: flex;
  gap: 15px;
}

.retry-btn,
.back-btn {
  padding: 10px 25px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 600;
  text-decoration: none;
}

.retry-btn {
  background-color: #3a8ee6;
  color: white;
  border: none;
}

.retry-btn:hover {
  background-color: #2c6eb9;
  transform: translateY(-3px);
}

.back-btn {
  background-color: #f0f0f0;
  color: #666;
  border: none;
}

.back-btn:hover {
  background-color: #e0e0e0;
  transform: translateY(-3px);
}

/* 产品内容 */
.product-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px 80px;
}

/* 产品基本信息 */
.product-basic {
  display: flex;
  gap: 40px;
  padding: 30px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
}

.product-gallery {
  flex: 0 0 45%;
}

.main-image {
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 15px;
  position: relative;
  height: 400px;
}

.main-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-thumbs {
  display: flex;
  gap: 10px;
}

.thumb {
  width: 80px;
  height: 80px;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s;
}

.thumb:hover {
  border-color: #3a8ee6;
}

.thumb.active {
  border-color: #3a8ee6;
  box-shadow: 0 0 0 2px rgba(58, 142, 230, 0.2);
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-info {
  flex: 1;
}

.product-name {
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 20px;
  font-weight: 600;
}

.product-meta {
  margin-bottom: 25px;
}

.meta-item {
  margin-bottom: 12px;
  display: flex;
}

.meta-label {
  color: #666;
  font-size: 0.95rem;
  width: 80px;
}

.meta-value {
  flex: 1;
  color: #333;
  font-size: 0.95rem;
}

.category-tag {
  display: inline-block;
  background-color: #e8f4ff;
  color: #3a8ee6;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.product-indications {
  margin-bottom: 30px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.product-indications h3 {
  color: #3a8ee6;
  margin-bottom: 10px;
  font-size: 1.1rem;
}

.indications-content {
  color: #666;
  line-height: 1.7;
}

.indications-content :deep(p) {
  margin: 0;
}

/* 产品详情 */
.product-details {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 40px;
}

.details-tabs {
  display: flex;
  border-bottom: 1px solid #f0f0f0;
}

.tab {
  padding: 15px 30px;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}

.tab:hover {
  color: #3a8ee6;
}

.tab.active {
  color: #3a8ee6;
}

.tab.active:after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: #3a8ee6;
}

.tab-content {
  padding: 30px;
}

.tab-pane {
  line-height: 1.8;
}

.html-content {
  color: #555;
}

.html-content :deep(p) {
  margin-bottom: 15px;
}

.html-content :deep(ul) {
  margin-left: 20px;
}

.html-content :deep(li) {
  margin-bottom: 8px;
}

.html-content :deep(strong) {
  color: #333;
}

/* 相关产品 */
.related-products {
  margin-top: 40px;
}

.related-products h2 {
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 25px;
  text-align: center;
  position: relative;
}

.related-products h2:after {
  content: '';
  display: block;
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #3a8ee6, #42b983);
  margin: 15px auto 0;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.product-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
}

.product-image {
  height: 200px;
  overflow: hidden;
  position: relative;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}

.product-hover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.product-card:hover .product-hover {
  opacity: 1;
}

.product-card:hover .product-image img {
  transform: scale(1.08);
}

.detail-view-btn {
  background-color: white;
  color: #333;
  border: none;
  padding: 10px 15px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 0.9rem;
  text-decoration: none;
}

.detail-view-btn:hover {
  background-color: #3a8ee6;
  color: white;
}

.product-info {
  padding: 20px;
}

.product-info h3 {
  font-size: 1.1rem;
  margin-bottom: 10px;
  color: #333;
  font-weight: 600;
}

/* 响应式设计 */
@media (max-width: 992px) {
  .product-basic {
    flex-direction: column;
  }
  
  .product-gallery {
    flex: 0 0 100%;
  }
  
  .main-image {
    height: 350px;
  }
  
  .related-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .product-name {
    font-size: 1.5rem;
  }
  
  .main-image {
    height: 280px;
  }
  
  .thumb {
    width: 60px;
    height: 60px;
  }
  
  .details-tabs {
    overflow-x: auto;
    white-space: nowrap;
  }
  
  .related-grid {
    grid-template-columns: 1fr;
  }
  
  .tab {
    padding: 15px 20px;
    font-size: 0.9rem;
  }
}
</style> 